// pages/admin/BlogForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MdSave, MdArrowBack } from 'react-icons/md';

export default function BlogForm() {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    author: 'MBBS Dunia',
    reading_time: 5,
    is_published: true,
    is_featured: false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Load existing blog (edit mode) ──
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await axios.get(`${base_url}/api/v1/blogs/admin/${id}`);
        if (res.data.success) {
          const b = res.data.data;
          setForm({
            title: b.title || '',
            slug: b.slug || '',
            excerpt: b.excerpt || '',
            content: b.content || '',
            category: b.category || '',
            author: b.author || 'MBBS Dunia',
            reading_time: b.reading_time || 5,
            is_published: !!b.is_published,
            is_featured: !!b.is_featured,
          });
          setPreview(b.image || '');
        }
      } catch (err) {
        console.error('Load blog error:', err);
        setError(err?.response?.data?.message || 'Failed to load blog');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, base_url]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({
      ...p,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    setPreview(URL.createObjectURL(f));
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.title.trim() || !form.content.trim()) {
      setError('Title and content are required');
      return;
    }

    setSaving(true);

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== undefined) fd.append(k, v);
      });
      if (imageFile) fd.append('image', imageFile);

      if (isEdit) {
        await axios.put(`${base_url}/api/v1/blogs/${id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post(`${base_url}/api/v1/blogs`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      navigate('/dashboard/blogs');
    } catch (err) {
      console.error('Save blog error:', err);
      setError(err?.response?.data?.message || 'Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid mt-5 text-center">
        <div className="spinner-border text-primary" />
        <div className="mt-2">Loading blog...</div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="container-fluid mt-3">
        <div className="card shadow border-0">
          <div className="card-header bg-white d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => navigate('/dashboard/blogs')}
            >
              <MdArrowBack />
            </button>
            <h4 className="fw-bold mb-0">
              {isEdit ? 'Edit Blog' : 'Create Blog'}
            </h4>
          </div>

          <form onSubmit={handleSubmit} className="card-body">
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="row g-3">
              {/* Title */}
              <div className="col-md-8">
                <label className="form-label fw-bold">
                  Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={form.title}
                  onChange={onChange}
                  required
                />
              </div>

              {/* Slug */}
              <div className="col-md-4">
                <label className="form-label">Slug (auto if empty)</label>
                <input
                  type="text"
                  className="form-control"
                  name="slug"
                  value={form.slug}
                  onChange={onChange}
                  placeholder="my-blog-post"
                />
              </div>

              {/* Category */}
              <div className="col-md-4">
                <label className="form-label">Category</label>
                <input
                  type="text"
                  className="form-control"
                  name="category"
                  value={form.category}
                  onChange={onChange}
                  placeholder="NEET, Colleges, Counselling..."
                />
              </div>

              {/* Author */}
              <div className="col-md-4">
                <label className="form-label">Author</label>
                <input
                  type="text"
                  className="form-control"
                  name="author"
                  value={form.author}
                  onChange={onChange}
                />
              </div>

              {/* Reading time */}
              <div className="col-md-4">
                <label className="form-label">Reading Time (minutes)</label>
                <input
                  type="number"
                  className="form-control"
                  name="reading_time"
                  min="1"
                  value={form.reading_time}
                  onChange={onChange}
                />
              </div>

              {/* Excerpt */}
              <div className="col-12">
                <label className="form-label">Excerpt (short description)</label>
                <textarea
                  className="form-control"
                  name="excerpt"
                  rows="2"
                  value={form.excerpt}
                  onChange={onChange}
                  placeholder="Brief summary shown on blog cards"
                />
              </div>

              {/* Content */}
              <div className="col-12">
                <label className="form-label fw-bold">
                  Content <span className="text-danger">*</span>{' '}
                  <span className="text-muted small">
                    (HTML supported: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;img&gt;)
                  </span>
                </label>
                <textarea
                  className="form-control font-monospace"
                  name="content"
                  rows="16"
                  value={form.content}
                  onChange={onChange}
                  placeholder="<h2>Heading</h2><p>Content...</p>"
                  required
                />
              </div>

              {/* Image */}
              <div className="col-md-6">
                <label className="form-label">Featured Image</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={onFileChange}
                />
                {preview && (
                  <div className="mt-2">
                    <img
                      src={preview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 220,
                        borderRadius: 8,
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Toggles */}
              <div className="col-md-6 d-flex flex-column justify-content-center gap-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="is_published"
                    name="is_published"
                    checked={form.is_published}
                    onChange={onChange}
                  />
                  <label className="form-check-label" htmlFor="is_published">
                    Published
                  </label>
                </div>

                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="is_featured"
                    name="is_featured"
                    checked={form.is_featured}
                    onChange={onChange}
                  />
                  <label className="form-check-label" htmlFor="is_featured">
                    Featured on Home Page
                  </label>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 mt-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                <MdSave />{' '}
                {saving ? 'Saving...' : isEdit ? 'Update Blog' : 'Create Blog'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/dashboard/mbbs/blogs')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}   